import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Target, Plus, Trash2, Flag, CheckCircle2, Loader2, Anchor } from "lucide-react";
import { toast } from "sonner";

function GoalDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", progress: 0, target_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.post("/goals", form); toast.success("Milestone charted"); setOpen(false); setForm({ title: "", description: "", progress: 0, target_date: "" }); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="add-goal-button"><Plus className="mr-2 h-4 w-4" />New Goal</Button></DialogTrigger>
      <DialogContent className="glass">
        <DialogHeader><DialogTitle className="font-display">Chart a New Milestone</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="goal-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div><Label>Target Date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="mt-1" /></div>
          <div>
            <Label>Initial Progress: {form.progress}%</Label>
            <Slider value={[form.progress]} onValueChange={([v]) => setForm({ ...form, progress: v })} max={100} step={5} className="mt-3" />
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-[hsl(var(--gold))] text-[#0A192F]" data-testid="goal-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProgressMap() {
  const { hasRole } = useAuth();
  const [goals, setGoals] = useState([]);
  const load = () => api.get("/goals").then((r) => setGoals(r.data.goals));
  useEffect(() => { load(); }, []);

  const updateProgress = async (g, progress) => {
    const { data } = await api.put(`/goals/${g.id}`, { progress });
    setGoals((gs) => gs.map((x) => x.id === g.id ? data.goal : x));
  };
  const del = async (id) => { await api.delete(`/goals/${id}`); toast.success("Removed"); load(); };

  const completed = goals.filter((g) => g.status === "completed").length;
  const overall = goals.length ? Math.round(goals.reduce((a, g) => a + g.progress, 0) / goals.length) : 0;

  return (
    <div>
      <PageHeader eyebrow="The Voyage" title="Progress Map"
        description="Organizational goals and milestones charted along the Galleon's course.">
        {hasRole("officer") && <GoalDialog onDone={load} />}
      </PageHeader>

      <Card className="gold-ring glass mb-8 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="label-eyebrow text-muted-foreground">Overall Fleet Progress</p>
            <p className="mt-1 font-display text-4xl font-800 gold-text">{overall}%</p>
          </div>
          <div className="flex gap-6 text-center">
            <div><p className="font-display text-2xl font-700 text-emerald-400">{completed}</p><p className="text-xs text-muted-foreground">Completed</p></div>
            <div><p className="font-display text-2xl font-700 text-[hsl(var(--water))]">{goals.length - completed}</p><p className="text-xs text-muted-foreground">In Progress</p></div>
          </div>
        </div>
        <Progress value={overall} className="mt-4 h-3" />
      </Card>

      <div className="relative space-y-6 before:absolute before:left-[21px] before:top-2 before:h-[calc(100%-1rem)] before:w-0.5 before:bg-[hsl(var(--gold))]/25">
        {goals.map((g) => (
          <div key={g.id} className="relative flex gap-5" data-testid={`goal-${g.id}`}>
            <div className={`z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full ring-4 ring-background ${g.status === "completed" ? "bg-emerald-500" : "bg-[hsl(var(--gold))]"}`}>
              {g.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-white" /> : <Flag className="h-5 w-5 text-[#0A192F]" />}
            </div>
            <Card className="gold-ring glass flex-1 p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-display text-lg font-700">{g.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {g.status === "completed" ? <Badge className="bg-emerald-500 text-white">Completed</Badge>
                    : <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]">Active</Badge>}
                  {hasRole("officer") && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(g.id)} data-testid={`delete-goal-${g.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
              <div className="mt-4">
                <div className="mb-1 flex justify-between text-xs"><span className="text-muted-foreground">Progress</span><span className="font-mono-data">{g.progress}%</span></div>
                <Progress value={g.progress} className="h-2" />
                {g.target_date && <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Anchor className="h-3 w-3" /> Target: {g.target_date}</p>}
                {hasRole("officer") && (
                  <div className="mt-3">
                    <Slider defaultValue={[g.progress]} max={100} step={5} onValueCommit={([v]) => updateProgress(g, v)} data-testid={`goal-slider-${g.id}`} />
                  </div>
                )}
              </div>
            </Card>
          </div>
        ))}
        {goals.length === 0 && <p className="text-sm text-muted-foreground">No milestones charted yet.</p>}
      </div>
    </div>
  );
}
